import { SOLVE_V2_BASE_URL } from '../constants';
import { dashboardApi } from '../dashboard-api';
import {
  BaseBrand,
  BaseVoiceBrand,
  Brand,
  BrandWithIntentUsage,
} from 'src/types/brandTypes';
import {
  WhitelistedDomainsRequest,
  WhitelistedDomainsResponse,
} from 'src/types/workflowBuilderAPITypes';

const brandApi = dashboardApi.injectEndpoints({
  endpoints: builder => ({
    createBrand: builder.mutation<Brand, BaseBrand>({
      invalidatesTags: ['Brands'],
      query: body => ({
        body,
        method: 'POST',
        url: `${SOLVE_V2_BASE_URL}/brand`,
      }),
    }),
    createVoiceBrand: builder.mutation<Brand, BaseVoiceBrand>({
      invalidatesTags: ['Brands'],
      query: body => ({
        body,
        method: 'POST',
        url: `${SOLVE_V2_BASE_URL}/voice/brand`,
      }),
    }),
    deleteBrand: builder.mutation<
      void,
      { brandId: string; brandType?: string }
    >({
      invalidatesTags: ['Brands'],
      query: ({ brandId, brandType }) => {
        const url = `${SOLVE_V2_BASE_URL}/brand/${brandId}`;
        return {
          method: 'DELETE',
          url: brandType ? `${url}?brand_type=${brandType}` : url,
        };
      },
    }),
    getBrands: builder.query<Brand[], { brandType?: string }>({
      providesTags: ['Brands'],
      query: ({ brandType }) => {
        const url = SOLVE_V2_BASE_URL + '/brands';
        if (brandType) {
          return `${url}?brand_type=${brandType}`;
        }
        return url;
      },
    }),
    getBrandsIntentUsage: builder.query<
      BrandWithIntentUsage[],
      { brandType?: string }
    >({
      providesTags: ['Brands'],
      query: ({ brandType }) => {
        const url = SOLVE_V2_BASE_URL + '/brands-intent-usage';
        if (brandType) {
          return `${url}?brand_type=${brandType}`;
        }
        return url;
      },
    }),
    getWhitelistedDomains: builder.query<WhitelistedDomainsResponse, void>({
      query: () => `${SOLVE_V2_BASE_URL}/whitelisted-domains`,
    }),
    updateBrand: builder.mutation<Brand, { body: BaseBrand; brandId: string }>({
      invalidatesTags: ['Brands'],
      query: ({ body, brandId }) => ({
        body,
        method: 'PUT',
        url: `${SOLVE_V2_BASE_URL}/brand/${brandId}`,
      }),
    }),
    updateBrandTags: builder.mutation<
      Brand,
      { body: string[]; brandId: string }
    >({
      invalidatesTags: ['Brands'],
      query: ({ body, brandId }) => ({
        body,
        method: 'PUT',
        url: `${SOLVE_V2_BASE_URL}/brand/${brandId}/tags`,
      }),
    }),
    updateWhitelistedDomains: builder.mutation<
      WhitelistedDomainsResponse,
      WhitelistedDomainsRequest
    >({
      async onQueryStarted(update, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          brandApi.util.updateQueryData(
            'getWhitelistedDomains',
            undefined,
            draft => {
              Object.assign(draft, update);
            },
          ),
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
      query: body => ({
        body,
        method: 'PUT',
        url: `${SOLVE_V2_BASE_URL}/whitelisted-domains`,
      }),
    }),
  }),
});

export const {
  useCreateBrandMutation,
  useCreateVoiceBrandMutation,
  useDeleteBrandMutation,
  useGetBrandsIntentUsageQuery,
  useGetBrandsQuery,
  useGetWhitelistedDomainsQuery,
  useUpdateBrandMutation,
  useUpdateBrandTagsMutation,
  useUpdateWhitelistedDomainsMutation,
} = brandApi;
